//--------------------------------------------------------------------------------------//
//                                                                                      //
//                                    Util Functions                                    //
//                                                                                      //
//--------------------------------------------------------------------------------------//

class Utils {

    // Fetch and Return Data from API
    static async fetchAPIData(api) {
        try {
            const response = await fetch(api);
            if (response.status !== 200) return null;
            const data = await response.json();
            return data;
        } catch (err) {
            return null;
        }
    }

    // Run Async Function and return Data
    static async asyncFunction(func) {
        try {
            const data = await func;
            return data;
        } catch (err) {
            return null;
        }
    }

    // Prepend 0
    static prepend0 (num) {
        let number = num;
        if (number % 1 !== 0) number = Math.abs(Math.trunc(number));
        return ("0" + number).slice(-2);
    }

    // Get Current Locale 
    static getCurrentLocale() {
        return document.documentElement.lang ? document.documentElement.lang : 'en';
    }

    static getCurrentCurrency() {
      const currency = document.documentElement.getAttribute('currency');
      return currency ? currency : 'CAD';
    }

    static localizeNumbers(value, isCurrency = false) {
      const currency = Utils.getCurrentCurrency();
      const currencyOption = isCurrency ? { style: 'currency', currency, maximumFractionDigits: 0, minimumFractionDigits: 0 } : {};
      return new Intl.NumberFormat(Utils.getCurrentLocale(), currencyOption).format(value).replace(/[A-Za-z]/g, "");
    }

    // Parse Prize Type
    static parsePrizeType(typeNum, isEarlyBird, separateEBandConLabel = true) {

        let message = '';

        switch (typeNum) {
            case 1: 
                message = langGrand;
                break;
            case 2:
                message =  separateEBandConLabel ? langCon : langEB;
                break;
            case 4:
                message = langJackpot;
                break;
            default:
                message = '';
        }

        if (isEarlyBird) {
            message = langEB;
        }

        return message;
    }

    /**
     * Sort Winners Data
     * 
     * Winners will be sorted differently depending on the data source.
     * Should the source be a CSV, it will simply reverse the winners data.
     * Else, it will reverse the winner based on the date.
     * 
     * @param {Array} winnersDataOrigin 
     * @returns 
     */

    static sortWinners(winnersDataOrigin) {

        if (guillotineWinnersFromCSV) {
            return winnersDataOrigin.reverse();
        }

        const orderedWinners = winnersDataOrigin.sort((a, b) => {
            const dateA = new Date(a.isoDrawDate);
            const dateB = new Date(b.isoDrawDate);

            return dateB - dateA;
        })

        return orderedWinners;
    }

    static createScript(properties) {
        const scriptEl = document.createElement('script');
        for (const key in properties) {
            scriptEl.setAttribute(key, properties[key]);
        }
        document.head.appendChild(scriptEl);
    }

}



//--------------------------------------------------------------------------------------//
//                                                                                      //
//                                     Vue Handler                                      //
//                                                                                      //
//--------------------------------------------------------------------------------------//


// Initialize Vue App under a controlled setting
async function initializeVueApp( passedOptions ) {

    const defaultOptions = {
        mixins: [],
        beforeCreateCallback: () => {},
        mountedCallback: () => {},
        id: '#vue_app'
    }

    const options = { ...defaultOptions, ...passedOptions };

    // Async Data to run first before initializing the app
    // Mimic Nuxt JS's Async Data behaviour in client side

    let asyncDataResult = {};

    await Promise.all([...options.mixins].map(async option => {
        if (option.asyncData) {
            const data = await Utils.asyncFunction(option.asyncData());
            asyncDataResult = {...asyncDataResult, ...data}
        }
    }));

    const mixinList = [...options.mixins].map(mixin => {
        if (mixin.asyncData)
            delete mixin.asyncData
        return mixin;
    });

    // Construct App Object with Data and Mixins implemented
    const CTA = {
        compilerOptions: {
            delimiters: ['[[', ']]'],
        },
        data: () => ({
            ...asyncDataResult,
        }),
        beforeCreate() {
            options.beforeCreateCallback(this, options);
        },
        mounted() {
            options.mountedCallback(this, options);
        },
        mixins: mixinList,
    }
    
    // Create Vue Instance
    const app = Vue.createApp(CTA);

    // Recognize every element starting with "afs-" to be a custom element
    app.config.isCustomElement = tag => tag.startsWith('afs-');

    // Mount App
    app.mount(options.id);

}

//--------------------------------------------------------------------------------------//
//                                                                                      //
//                                      Vue Mixins                                      //
//                                                                                      //
//--------------------------------------------------------------------------------------//

//--------------------------------------------------------------------------------------//
//                                     Setup Mixin                                      //
//--------------------------------------------------------------------------------------//

 const setupMixin = {
    data() {
        return {
            isOntario: isOntario,
            domainFragment: domainFragment,
            ebLabelSeperationPrizeBox: ebLabelSeperationPrizeBox,
            ebLabelSeperationWinnerSection: ebLabelSeperationWinnerSection,
            ebCountdownToEb: ebCountdownToEb,
            dateTime: null,
            getUserGeocoordinates: getUserGeocoordinates
        }
    },
    methods: {
        parsePrizeType: Utils.parsePrizeType,
        getCurrentLocale: Utils.getCurrentLocale,
        localizeNumbers: Utils.localizeNumbers,
    }
}

//--------------------------------------------------------------------------------------//
//                                   Translate Mixin                                    //
//--------------------------------------------------------------------------------------//

const translateMixin = {
    data() {
        return {
            countdownFormat: langCountdownFormat,
            deadlineFormat: langDeadlineFormat,
            winnerDrawdateFormat: langWinnerDrawdateFormat,
        }
    },
    beforeCreate() {
        luxon.Settings.defaultLocale = Utils.getCurrentLocale();
    },
    computed: {
        pageIsFrench() {
            const pageLocale = this.getCurrentLocale();
            return (pageLocale === "fr" || pageLocale.includes("fr")) ? true : false;
        }
    }
}

//--------------------------------------------------------------------------------------//
//                                      Prize Mixin                                     //
//--------------------------------------------------------------------------------------//

/**
 * Prize Mixin
 * 
 * Is responsible for the prizes displayed on the page
 * @mixin
 */
const prizeMixin = {
    async asyncData() {
        let prizeData = null;
        
        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].prizes) {
            prizeData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].prizes;
        } else {
            prizeData = await Utils.fetchAPIData(`${guillotineRootDomain}/getprizes`);
        }

        return {prizeData: prizeData}
    },
    data() {
        return {
            prizeData: null,
        }
    },
    methods: {
        formatDeadlineDate(dateTimeString) {
            return luxon.DateTime.fromISO(dateTimeString).toFormat(this.deadlineFormat);
        },
        formatPrizeType(prize) {
            return Utils.parsePrizeType(prize.prizeTypeId, prize.isEarlyBird, ebLabelSeperationPrizeBox);
        }
    },
    computed: {
        allEarlyBird() {
            if (!this.prizeData || !this.currentTime) return [];

            const otherPrizeList = [...this.prizeData].filter(prize => !prize.isEarlyBird && prize.prizeTypeId == 2);

            return [...this.allEarlyBirdWithDeadline, ...otherPrizeList];
        },
        allEarlyBirdWithDeadline() {
            if (!this.prizeData || !this.currentTime) return [];

            const ebList = [...this.prizeData]
                .filter(prize => prize.isEarlyBird && luxon.DateTime.fromISO(prize.deadline).diff(this.currentTime).toMillis() > 0)
                .sort((a, b) => {
                    return luxon.DateTime.fromISO(a.deadline) - luxon.DateTime.fromISO(b.deadline);
                });

            return ebList;
        }
    }
}

//--------------------------------------------------------------------------------------//
//                                      Time Mixin                                      //
//--------------------------------------------------------------------------------------//

const timeEventMixin = {
    async asyncData() {
        let eventData = null;
        let currentTime = null;
        let eventEndTime = null;
        let eventIsHidden = true;

        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].event) {
            eventData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].event;
        } else {
            eventData = await Utils.fetchAPIData(`${guillotineRootDomain}/getevent`);
        }


        const DateTime = luxon.DateTime;
        if (eventData) {
            currentTime = useMockData ? DateTime.local() : DateTime.fromISO(eventData.currentTime);

            eventEndTime = DateTime.fromISO(eventData.salesEnd);
            startTime = DateTime.fromISO(eventData.salesStart);
            if (startTime.diff(currentTime).toMillis() < 0 && eventEndTime.diff(currentTime).toMillis() > 0) {
                eventIsHidden = false;
            }
        }

        return {
            eventData: eventData,
            currentTime: currentTime,
            currentEndTime: eventEndTime,
            eventIsHidden: eventIsHidden,
            endTimesQueue: [eventEndTime],
        }
    },
    data() {
        return {
            eventData: null,
            currentTime: null,
            currentEndTime: null,
            timeStructure: ['weeks', 'days', 'hours', 'minutes', 'seconds'],
            timeLanguage: [langCountdownWeeks, langCountdownDays, langCountdownHours, langCountdownMin, langCountdownSec],
            eventIsHidden: true,
            eventInterval: null,
            endTimesQueue: [],
        }
    },
    async created() {
        if (!this.eventData || this.eventIsHidden) return

        if (this.ebCountdownToEb) {
            // Update endTimesQueue with early bird deadlines
            const earlyBirdDeadlines = this.allEarlyBirdWithDeadline.map(eb => luxon.DateTime.fromISO(eb.deadline));
            this.endTimesQueue = [...earlyBirdDeadlines, ...this.endTimesQueue];
        }
        this.currentEndTime = this.endTimesQueue[0];

        this.eventInterval = setInterval(() => {
            this.currentTime = this.currentTime.plus({seconds: 1});
            if (this.currentEndTime.diff(this.currentTime).toMillis() < 0) {

                this.endTimesQueue.shift();

                if (this.endTimesQueue.length < 1) {
                    this.eventIsHidden = true;
                } else {
                    this.currentEndTime = this.endTimesQueue[0];
                }

            }
        }, 1000);
    },
    methods: {
        resetTime() {
            return {
                weeks: 0,
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
            }
        },
        prepend0: Utils.prepend0,
        getCountdownLabelName(structure) {
            const indexOfStructure = this.timeStructure.indexOf(structure);
            return this.timeLanguage[indexOfStructure];
        }
    },
    computed: {
        isCountingDownEB() {
            if (this.endTimesQueue.length < 2) return false;
            return true;
        },
        timeData() {
            if (!this.currentEndTime) return this.resetTime();
            const time = this.currentEndTime.diff(this.currentTime, this.timeStructure);
            if (time.seconds < 0) {
                return this.resetTime();
            }
            return time;
        },
        previousTimeData() {
            if (!this.currentEndTime) return this.resetTime();
            const time = this.currentEndTime.diff(this.currentTime.minus({seconds: 1}), this.timeStructure);
            if (time.seconds < 0) {
                return this.resetTime();
            }
            return time;
        },
        comparedTimeData() {
            const timeData = this.timeData;
            const previousTimeData = this.previousTimeData;
            const differentTimeStructure = [];
            this.timeStructure.forEach(structure => {
                if (timeData[structure] !== previousTimeData[structure]){
                    differentTimeStructure.push(structure);
                }
            });
            return differentTimeStructure;
        },
        salesEndDateTimeFormat() {
            if (!this.eventData) return;
            return this.currentEndTime.toFormat(this.countdownFormat);
        }
    },
    watch: {
        currentTime() {
            const comparedTimeStructure = this.comparedTimeData;
            if (this.eventIsHidden) return;

            for(let structure of comparedTimeStructure) {
                const currentStructureEl = this.$refs[structure][0];
                currentStructureEl.classList.remove('flip');
                setTimeout(() => currentStructureEl.classList.add('flip'), 50);
            }
        },
        eventIsHidden() {
            if (this.eventIsHidden) {
                clearInterval(this.eventInterval);
            }
        }
    }
}

//--------------------------------------------------------------------------------------//
//                                      Tier Mixin                                      //
//--------------------------------------------------------------------------------------//

const tierMixin = {
    async asyncData() {
        let fetchedTierData = null;

        const ppWrapper = document.querySelector('#price-point-buttons-wrapper');

        const ppTemplateShopify = document.querySelector('#price-point-buttons-template-shopify');
        const ppTemplateShopifyContent = ppTemplateShopify.content;
        ppTemplateShopify.remove();

        const ppTemplateVue = document.querySelector('#price-point-buttons-template-vue');
        const ppTemplateVueContent = ppTemplateVue.content;
        ppTemplateVue.remove();

        if (window.Shopify) {
            fetchedTierData = await Utils.fetchAPIData(`${guillotineRootDomain}/getpricepoints`);
            ppWrapper.appendChild(ppTemplateShopifyContent);
        } else {
            if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pricePoints) {
                fetchedTierData = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pricePoints;
            } else {
                fetchedTierData = await Utils.fetchAPIData(`${guillotineRootDomain}/getpricepoints`);
            }
            ppWrapper.appendChild(ppTemplateVueContent);
        } 
        
        return {
            pricePointData: fetchedTierData,
        }
    },
    data() {
        return {
            domainFragment: domainFragment,
            shopifyDomainFragment: shopifyDomainFragment,
            cartIsUpdating: false,
            defaultPricePointLimit: defaultPricePointLimit,
        }
    },
    mounted() {
        if (this.eventIsHidden) return;

        const buyButton = this.$refs.buyButton;
        buyButton.addEventListener('buyButtonTriggered', async (e) => {
            if (this.cartIsUpdating) return;

            if (window.Shopify) {
                this.cartIsUpdating = true;
                await this.purchaseProducts(e.detail.products);
                this.cartIsUpdating = false;
            } else {
                console.log("Purchase will only run in Shopify");
            }
        });
    },
    computed: {
        isShopify() {
            if (this.filteredPricePointData.length < 1) return;
            const filteredShopifyPP = this.filteredPricePointData.filter(pricePoint => pricePoint.shopifyVariantId);
            return filteredShopifyPP.length > 0;
        },
        filteredPricePointData() {
            if (!this.pricePointData) return [];
            return [...this.pricePointData].filter(pricePoint => pricePoint.display);
        },
        pricePointLimit() {
            if (!this.eventData) return this.defaultPricePointLimit;
            if (!("maxTicketsForOnline" in this.eventData)) return this.defaultPricePointLimit;

            return this.eventData.maxTicketsForOnline;
        }
    },
    methods: {
        async purchaseProducts(products) {
            try {
                const parsedProducts = products.map(product => {
                    return {
                        id: parseInt(product.hrefFragment),
                        quantity: product.quantity,
                    }
                });

                const cartData = {
                    items: parsedProducts,
                    attributes: {
                        card_number: null,
                    }
                }

                if (getUserGeocoordinates) {
                    await (() => new Promise(res => {
                        navigator.geolocation.getCurrentPosition(
                            position => {
                                cartData.attributes.coords_lat = position.coords.latitude
                                cartData.attributes.coords_long = position.coords.longitude
                                res(true);
                            },
                            () => res(false),
                        )
                    }))();
                }

                await fetch('/cart/clear.js');
                await fetch('/cart/add.js', {
                    method: 'post',
                    headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(cartData),
                });

                window.location.href = `${Shopify.routes.root}checkout`;
            } catch (err) {
                console.warn('Purchase has failed: ' + err);
            }
        }
    }
}

//--------------------------------------------------------------------------------------//
//                                    Jackpot Mixin                                     //
//--------------------------------------------------------------------------------------//

const jackpotMixin = {
    async asyncData() {
        let data = null;
        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pot) {
            data = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].pot;
        } else {
            data = await Utils.fetchAPIData(`${guillotineRootDomain}/getpot`);
        }

        return {potData: data};
    },
    async created() {
        if (useMockData) return;
        setInterval(async () => {
            const data = await Utils.fetchAPIData(`${guillotineRootDomain}/getpot`)
            this.potData = data;
        }, 60000);
    },
}

//--------------------------------------------------------------------------------------//
//                                    Winners Mixin                                     //
//--------------------------------------------------------------------------------------//

const winnersMixin = {
    async asyncData() {
        let data = null;
        if (useMockData && mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].recentWinners) {  
            data = mock[Utils.getCurrentLocale()] && mock[Utils.getCurrentLocale()].recentWinners;
        } else {
            data = await Utils.fetchAPIData(`${guillotineRootDomain}/getrecentwinners`);
        }

        const sortedWinnersData = Utils.sortWinners(data);

        sortedWinnersData.map(winnerData => {
            Object.assign(winnerData, {
                currentPrizeFormat: Utils.parsePrizeType(winnerData.prizeTypeId, winnerData.isEarlyBird, ebLabelSeperationWinnerSection)
            })
        })
        
        return {
            winnersData: sortedWinnersData,
        }
    },
    created () {
        if (!this.winnersData) this.winnersData = [];
        this.winnersData.forEach(winner => {
            if (winner.name && winner.name == 'Name Name') {
                winner.name = null;
            }
        })
    },
    methods: {
        getWinnerDrawDate(winner) {
            return luxon.DateTime.fromISO(winner.isoDrawDate).toFormat(this.winnerDrawdateFormat);
        },
        getWinnerPrize(winner) {
            return winner.prizeAmount && winner.prizeAmount > 0 ? this.localizeNumbers(parseFloat(winner.prizeAmount), true) : winner.prizeTitle;
        }
    },
    computed: {
        latestWinners() {
            const DateTime = luxon.DateTime;
            let latestWinners = [...this.winnersData].filter(winner => {
                const currentWinnerDateTime = DateTime.fromISO(winner.isoDrawDate);
                const latestWinnerDateTime = DateTime.fromISO(this.winnersData[0].isoDrawDate);

                if (
                    currentWinnerDateTime.day == latestWinnerDateTime.day 
                    && currentWinnerDateTime.month == latestWinnerDateTime.month 
                    && currentWinnerDateTime.year == latestWinnerDateTime.year
                )
                    return true
                else
                    return false
            });
            return latestWinners;
        },
        latestWinnersPrizeFormat() {
            return this.latestWinners[0].currentPrizeFormat;
        }
    }
}

//--------------------------------------------------------------------------------------//
//                                                                                      //
//                                         End                                          //
//                                                                                      //
//--------------------------------------------------------------------------------------//
